import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../Button'
import { Icon, type IconName } from '../Icon'
import { Modal, type Tone } from './Modal'
import css from './Overlay.module.css'

/**
 * Dialog dan notifikasi.
 *
 * API-nya imperatif dan dapat dipanggil dari mana saja, termasuk dari luar
 * komponen React:
 *
 *   await uiConfirm({ title: 'Hapus berkas?', tone: 'danger' })
 *   const nama = await uiPrompt({ title: 'Nama folder baru' })
 *   toast.success('Berkas tersimpan')
 *
 * Ini menggantikan window.confirm dan window.prompt bawaan peramban, yang
 * tampilannya di luar kendali, tidak bisa diberi gaya, memblokir seluruh
 * halaman, dan di beberapa peramban seluler menampilkan nama domain di
 * dalam kotaknya sehingga terlihat seperti penipuan.
 */

// ── Jenis ───────────────────────────────────────────────────────────────────

interface ConfirmOptions {
  title: string
  description?: string
  tone?: Tone
  icon?: IconName
  confirmLabel?: string
  cancelLabel?: string
}

interface PromptOptions {
  title: string
  description?: string
  label?: string
  defaultValue?: string
  placeholder?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Kembalikan pesan galat untuk menolak, atau null bila nilainya sah. */
  validate?: (value: string) => string | null
}

interface AlertOptions {
  title: string
  description?: string
  tone?: Tone
  icon?: IconName
  closeLabel?: string
}

type ToastKind = 'info' | 'success' | 'error' | 'warning'

interface ToastOptions {
  detail?: string
  /** Lama tampil dalam milidetik; 0 berarti menetap sampai ditutup. */
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastItem extends ToastOptions {
  id: number
  kind: ToastKind
  title: string
  leaving?: boolean
}

type Permintaan =
  | { jenis: 'confirm'; opsi: ConfirmOptions; selesai: (v: boolean) => void }
  | { jenis: 'prompt'; opsi: PromptOptions; selesai: (v: string | null) => void }
  | { jenis: 'alert'; opsi: AlertOptions; selesai: () => void }

// ── Saluran antara API imperatif dan komponen host ──────────────────────────

let dorongDialog: ((p: Permintaan) => void) | null = null
let dorongToast: ((t: Omit<ToastItem, 'id'>) => void) | null = null

const antrianAwal: Permintaan[] = []
const antrianToastAwal: Omit<ToastItem, 'id'>[] = []

export function uiConfirm(opsi: ConfirmOptions): Promise<boolean> {
  return new Promise((selesai) => {
    const p: Permintaan = { jenis: 'confirm', opsi, selesai }
    // Panggilan yang terjadi sebelum host sempat terpasang tetap tersimpan,
    // sehingga tidak ada dialog yang hilang begitu saja.
    dorongDialog ? dorongDialog(p) : antrianAwal.push(p)
  })
}

export function uiPrompt(opsi: PromptOptions): Promise<string | null> {
  return new Promise((selesai) => {
    const p: Permintaan = { jenis: 'prompt', opsi, selesai }
    dorongDialog ? dorongDialog(p) : antrianAwal.push(p)
  })
}

export function uiAlert(opsi: AlertOptions): Promise<void> {
  return new Promise((selesai) => {
    const p: Permintaan = { jenis: 'alert', opsi, selesai }
    dorongDialog ? dorongDialog(p) : antrianAwal.push(p)
  })
}

function kirimToast(kind: ToastKind, title: string, opsi: ToastOptions = {}) {
  const t = { kind, title, ...opsi }
  dorongToast ? dorongToast(t) : antrianToastAwal.push(t)
}

export const toast = {
  info: (title: string, opsi?: ToastOptions) => kirimToast('info', title, opsi),
  success: (title: string, opsi?: ToastOptions) => kirimToast('success', title, opsi),
  warning: (title: string, opsi?: ToastOptions) => kirimToast('warning', title, opsi),
  /** Galat menetap sampai ditutup: pesan kegagalan tidak boleh lewat tanpa terbaca. */
  error: (title: string, opsi?: ToastOptions) =>
    kirimToast('error', title, { duration: 0, ...opsi }),
}

// ── Host ────────────────────────────────────────────────────────────────────

const IKON_TOAST: Record<ToastKind, IconName> = {
  info: 'info',
  success: 'success',
  error: 'error',
  warning: 'warning',
}

const DURASI_BAWAAN = 4500

/** Dipasang sekali di akar aplikasi. */
export function OverlayHost() {
  const [dialog, setDialog] = useState<Permintaan | null>(null)
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const idBerikut = useRef(1)
  const timerRef = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    dorongDialog = (p) => setDialog((sekarang) => sekarang ?? p)

    dorongToast = (t) => {
      const id = idBerikut.current++
      setToasts((daftar) => {
        // Batasi jumlah yang tampak; yang terlama dibuang lebih dulu.
        const berikut = [...daftar, { ...t, id }]
        return berikut.length > 4 ? berikut.slice(berikut.length - 4) : berikut
      })

      const durasi = t.duration ?? DURASI_BAWAAN
      if (durasi > 0) {
        timerRef.current.set(
          id,
          setTimeout(() => tutupToast(id), durasi),
        )
      }
    }

    // Kuras antrean yang sempat menumpuk sebelum host terpasang.
    const tertunda = antrianAwal.splice(0)
    if (tertunda.length > 0) setDialog(tertunda[0] as Permintaan)
    antrianToastAwal.splice(0).forEach((t) => dorongToast?.(t))

    const timers = timerRef.current
    return () => {
      dorongDialog = null
      dorongToast = null
      timers.forEach(clearTimeout)
      timers.clear()
    }
  }, [])

  function tutupToast(id: number) {
    const timer = timerRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timerRef.current.delete(id)
    }
    setToasts((daftar) => daftar.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
    setTimeout(() => setToasts((daftar) => daftar.filter((t) => t.id !== id)), 160)
  }

  return (
    <>
      {dialog && <DialogAktif permintaan={dialog} onTutup={() => setDialog(null)} />}
      {createPortal(
        <div
          className={css.toastRegion}
          /* polite, bukan assertive: notifikasi tidak boleh memotong pembacaan
             yang sedang berlangsung kecuali benar-benar mendesak. */
          role="status"
          aria-live="polite"
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`${css.toast} ${css[t.kind]} ${t.leaving ? css.leaving : ''}`}
            >
              <span className={`${css.toastIcon} ${css[t.kind]}`}>
                <Icon name={IKON_TOAST[t.kind]} size={18} />
              </span>
              <div className={css.toastBody}>
                <div className={css.toastTitle}>{t.title}</div>
                {t.detail && <div className={css.toastDetail}>{t.detail}</div>}
              </div>
              {t.action && (
                <button
                  type="button"
                  className={css.toastAction}
                  onClick={() => {
                    t.action?.onClick()
                    tutupToast(t.id)
                  }}
                >
                  {t.action.label}
                </button>
              )}
              <Button
                variant="ghost"
                size="sm"
                icon="close"
                aria-label="Tutup notifikasi"
                onClick={() => tutupToast(t.id)}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  )
}

function DialogAktif({
  permintaan,
  onTutup,
}: {
  permintaan: Permintaan
  onTutup: () => void
}) {
  const [nilai, setNilai] = useState(
    permintaan.jenis === 'prompt' ? (permintaan.opsi.defaultValue ?? '') : '',
  )
  const [galat, setGalat] = useState<string | null>(null)

  const selesaikan = (aksi: () => void) => {
    aksi()
    onTutup()
  }

  if (permintaan.jenis === 'alert') {
    const { opsi, selesai } = permintaan
    return (
      <Modal
        open
        onClose={() => selesaikan(selesai)}
        title={opsi.title}
        {...(opsi.description !== undefined ? { description: opsi.description } : {})}
        tone={opsi.tone ?? 'info'}
        icon={opsi.icon ?? 'info'}
        footer={
          <Button variant="primary" onClick={() => selesaikan(selesai)}>
            {opsi.closeLabel ?? 'Mengerti'}
          </Button>
        }
      />
    )
  }

  if (permintaan.jenis === 'confirm') {
    const { opsi, selesai } = permintaan
    const tone = opsi.tone ?? 'warning'
    return (
      <Modal
        open
        onClose={() => selesaikan(() => selesai(false))}
        title={opsi.title}
        {...(opsi.description !== undefined ? { description: opsi.description } : {})}
        tone={tone}
        icon={opsi.icon ?? (tone === 'danger' ? 'warning' : 'info')}
        footer={
          <>
            <Button variant="ghost" onClick={() => selesaikan(() => selesai(false))}>
              {opsi.cancelLabel ?? 'Batal'}
            </Button>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => selesaikan(() => selesai(true))}
            >
              {opsi.confirmLabel ?? 'Lanjutkan'}
            </Button>
          </>
        }
      />
    )
  }

  const { opsi, selesai } = permintaan

  const kirim = () => {
    const bersih = nilai.trim()
    const pesan = opsi.validate?.(bersih) ?? null
    if (pesan) {
      setGalat(pesan)
      return
    }
    selesaikan(() => selesai(bersih))
  }

  return (
    <Modal
      open
      onClose={() => selesaikan(() => selesai(null))}
      title={opsi.title}
      {...(opsi.description !== undefined ? { description: opsi.description } : {})}
      icon="edit"
      footer={
        <>
          <Button variant="ghost" onClick={() => selesaikan(() => selesai(null))}>
            {opsi.cancelLabel ?? 'Batal'}
          </Button>
          <Button variant="primary" onClick={kirim}>
            {opsi.confirmLabel ?? 'Simpan'}
          </Button>
        </>
      }
    >
      <div className={css.field}>
        {opsi.label && (
          <label
            htmlFor="ui-prompt-input"
            style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-2)',
            }}
          >
            {opsi.label}
          </label>
        )}
        <input
          id="ui-prompt-input"
          value={nilai}
          placeholder={opsi.placeholder}
          onChange={(e) => {
            setNilai(e.target.value)
            if (galat) setGalat(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              kirim()
            }
          }}
          aria-invalid={galat ? true : undefined}
          aria-describedby={galat ? 'ui-prompt-error' : undefined}
        />
        {galat && (
          <div className={css.fieldError} id="ui-prompt-error" role="alert">
            {galat}
          </div>
        )}
      </div>
    </Modal>
  )
}

export { Modal }
export type { Tone }
